import React, { useEffect, useMemo, useState } from 'react';
import { Download, Search } from 'lucide-react';
import * as XLSX from 'xlsx';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
};

const stripHtmlToText = (value) => {
  if (!value) return '';

  if (typeof window !== 'undefined' && window.DOMParser) {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(value, 'text/html');
    return (doc.body.textContent || '').trim();
  }

  return String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const truncateText = (value, maxLength = 80) => {
  if (!value) return '-';
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}...`;
};

const normalizeText = (value) => String(value || '').toLowerCase();

const getStatusBadgeClasses = (status, isDarkMode) => {
  const normalizedStatus = normalizeText(status);

  if (normalizedStatus.includes('documentado')) {
    return isDarkMode
      ? 'bg-sky-500/20 text-sky-300 border border-sky-400/45'
      : 'bg-sky-100 text-sky-700 border border-sky-300';
  }

  if (normalizedStatus.includes('aprobacion')) {
    return isDarkMode
      ? 'bg-amber-500/20 text-amber-300 border border-amber-400/45'
      : 'bg-amber-100 text-amber-700 border border-amber-300';
  }

  if (normalizedStatus.includes('aprobado')) {
    return isDarkMode
      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/45'
      : 'bg-emerald-100 text-emerald-700 border border-emerald-300';
  }

  if (normalizedStatus.includes('rechazado')) {
    return isDarkMode
      ? 'bg-rose-500/20 text-rose-300 border border-rose-400/45'
      : 'bg-rose-100 text-rose-700 border border-rose-300';
  }

  return isDarkMode
    ? 'bg-slate-500/20 text-slate-300 border border-slate-400/45'
    : 'bg-slate-100 text-slate-700 border border-slate-300';
};

export default function DetalleRegistrosHorasTable({
  records = [],
  isDarkMode = true
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const normalizedRecords = useMemo(() => {
    return records.map((record) => {
      const detalleLimpio = stripHtmlToText(record.detalle_actividad_html);
      return {
        ...record,
        fecha_formateada: formatDate(record.fecha_inicio),
        horas_formateadas: `${Number(record.tiempo_empleado_hrs || 0).toLocaleString('es-MX', {
          maximumFractionDigits: 2
        })} hrs`,
        detalle_limpio: detalleLimpio
      };
    });
  }, [records]);

  const filteredRecords = useMemo(() => {
    const term = normalizeText(searchTerm).trim();
    if (!term) return normalizedRecords;

    return normalizedRecords.filter((record) => {
      return (
        normalizeText(record.id_registro_tiempo).includes(term) ||
        normalizeText(record.numero_sap).includes(term) ||
        normalizeText(record.nombre_colaborador).includes(term) ||
        normalizeText(record.cliente).includes(term) ||
        normalizeText(record.tipo_actividad).includes(term)
      );
    });
  }, [normalizedRecords, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / rowsPerPage));

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRecords, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm, records]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleExportExcel = () => {
    const rowsToExport = filteredRecords.map((record) => ({
      Fecha: record.fecha_formateada,
      'ID Registro': record.id_registro_tiempo || '-',
      Tecnico: record.nombre_colaborador || '-',
      Cliente: record.cliente || '-',
      Horas: record.horas_formateadas,
      'Tipo Actividad': record.tipo_actividad || '-',
      Estatus: record.estatus || '-',
      Detalle: record.detalle_limpio || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Detalle');

    const now = new Date();
    const fileDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    XLSX.writeFile(workbook, `timesheets_detalle_${fileDate}.xlsx`);
  };

  const containerClasses = isDarkMode
    ? 'bg-[#00261A] border border-[#00FF66]/25 rounded-xl p-6 shadow-lg'
    : 'bg-white border border-emerald-200 rounded-xl p-6 shadow-lg';

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-white/75' : 'text-gray-600';
  const tableHeader = isDarkMode ? 'bg-[#00FF66]/8 text-[#bde8cc]' : 'bg-emerald-50 text-gray-700';
  const tableBorder = isDarkMode ? 'border-[#00FF66]/15' : 'border-gray-200';
  const rowHover = isDarkMode ? 'hover:bg-[#00FF66]/6' : 'hover:bg-emerald-50';

  const startRecord = filteredRecords.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, filteredRecords.length);

  return (
    <section className={containerClasses}>
      <div className="mb-5">
        <h3 className={isDarkMode ? 'text-[#00FF66] text-xl font-bold' : 'text-emerald-700 text-xl font-bold'}>
          Detalle de Registros de Horas
        </h3>
        <p className={`${textSecondary} text-sm mt-1`}>
          Registros capturados por los colaboradores segun los filtros seleccionados.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between mb-4">
        <div className="relative w-full lg:max-w-md">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-[#1EEB8A]' : 'text-emerald-500'}`} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por ID Registro, Tecnico, Cliente o Tipo Actividad"
            className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${
              isDarkMode
                ? 'bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] placeholder:text-[#9ac9aa] focus:border-[#00FF66] focus:shadow-[0_0_8px_rgba(0,255,102,0.2)]'
                : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-emerald-500'
            } focus:outline-none [transition:all_.25s_ease]`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`${textSecondary} text-sm`}>Registros por pagina</span>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className={`px-2 py-2 rounded-lg border text-sm ${
                isDarkMode
                  ? 'bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] focus:border-[#00FF66]'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
              } focus:outline-none`}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExportExcel}
            disabled={filteredRecords.length === 0}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold [transition:all_.25s_ease] ${
              isDarkMode
                ? 'bg-[#00FF66]/10 border-[#00FF66]/35 text-[#00FF66] hover:bg-[#00FF66]/18 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      {filteredRecords.length === 0 ? (
        <div className={`rounded-lg border ${tableBorder} px-4 py-10 text-center ${textSecondary}`}>
          No existen registros para los filtros seleccionados
        </div>
      ) : (
        <>
          <div className="overflow-x-auto [scrollbar-gutter:stable]">
            <table className="min-w-[1080px] w-full text-sm">
              <thead className={`${tableHeader} sticky top-0 z-10`}>
                <tr>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Fecha</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>ID Registro</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Tecnico</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Cliente</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Horas</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Tipo Actividad</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Estatus</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.map((record) => {
                  const statusLabel = record.estatus || 'SIN ESTATUS';
                  const detalleValue = record.detalle_limpio || '-';
                  return (
                    <tr
                      key={`${record.id_registro_tiempo}-${record.numero_sap}-${record.fecha_inicio}`}
                      className={`${rowHover} border-b ${tableBorder} [transition:background_.2s_ease]`}
                    >
                      <td className={`px-3 py-3 ${textPrimary}`}>{record.fecha_formateada}</td>
                      <td className={`px-3 py-3 ${textPrimary}`}>{record.id_registro_tiempo || '-'}</td>
                      <td className={`px-3 py-3 ${textPrimary}`}>{record.nombre_colaborador || '-'}</td>
                      <td className={`px-3 py-3 ${textPrimary}`}>{record.cliente || '-'}</td>
                      <td className={`px-3 py-3 ${textPrimary} font-semibold`}>{record.horas_formateadas}</td>
                      <td className={`px-3 py-3 ${textPrimary}`}>{record.tipo_actividad || '-'}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClasses(statusLabel, isDarkMode)}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className={`px-3 py-3 ${textPrimary}`} title={detalleValue}>
                        {truncateText(detalleValue, 80)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mt-4">
            <p className={`${textSecondary} text-sm`}>
              Mostrando {startRecord} - {endRecord} de {filteredRecords.length} registros
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'border-[#00FF66]/25 text-[#bde8cc] hover:border-[#00FF66]/45 disabled:opacity-40'
                    : 'border-gray-300 text-gray-700 hover:border-emerald-400 disabled:opacity-40'
                }`}
              >
                Anterior
              </button>
              <span className={`${textPrimary} text-sm font-semibold`}>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'border-[#00FF66]/25 text-[#bde8cc] hover:border-[#00FF66]/45 disabled:opacity-40'
                    : 'border-gray-300 text-gray-700 hover:border-emerald-400 disabled:opacity-40'
                }`}
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
